import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { computeMonthly } from "@/lib/lease";
import { GitCompare, X, Check, ArrowLeft, ShoppingCart, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * ComparePage, side-by-side comparison of up to 3 hardware items.
 * Reads IDs from ?items=id1,id2,id3
 */
export default function ComparePage() {
  const [params] = useSearchParams();
  const ids = (params.get("items") || "").split(",").filter(Boolean);
  const { leaseSettings, formatPrice, removeCompare, addToCart, compare } = useApp();
  const [items, setItems] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [hwResults, adRes] = await Promise.all([
          Promise.all(ids.map((id) => api.get(`/hardware/${id}`).then((r) => r.data).catch(() => null))),
          api.get("/addons"),
        ]);
        setItems(hwResults.filter(Boolean));
        setAddons(adRes.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [params.get("items")]); // eslint-disable-line

  // Build comparison rows
  const rows = useMemo(() => buildRows(items, addons, leaseSettings, formatPrice), [items, addons, leaseSettings, formatPrice]);

  const addHardwareToCart = (h) => {
    addToCart({
      type: "hardware",
      ref_id: h.id,
      sku: h.sku,
      name: h.name,
      unit_price: h.purchase_price || 0,
      monthly_price: 0,
      pricing_mode: "buy",
    });
    toast.success(`${h.name} toegevoegd aan winkelwagen`);
  };

  if (loading) return <div className="container-x py-20 text-[#A8B3AD]">Vergelijking laden…</div>;
  if (items.length === 0) {
    return (
      <div className="container-x py-20 text-center">
        <GitCompare className="w-12 h-12 text-[#5d6a64] mx-auto mb-4" />
        <h1 className="text-2xl text-white font-semibold mb-2">Geen kiosken om te vergelijken</h1>
        <p className="text-[#A8B3AD] mb-6">Ga naar de catalogus en klik op "Vergelijk" bij 2 of 3 kiosken.</p>
        <Link to="/catalog" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#D97742] text-white font-semibold hover:bg-[#c46939]">
          <ArrowLeft className="w-4 h-4" /> Naar catalogus
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="compare-page">
      {/* Hero */}
      <section className="container-x pt-10 pb-6">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#D97742] mb-2">
          <GitCompare className="w-3.5 h-3.5" /> Vergelijken
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold text-white">
          {items.length} kiosken naast elkaar
        </h1>
        <p className="text-sm text-[#A8B3AD] mt-2 max-w-2xl">
          Verschillende rijen worden in <span className="text-[#D97742] font-semibold">oranje</span> gemarkeerd zodat je in één oogopslag ziet waar de modellen van elkaar afwijken.
        </p>
      </section>

      {/* Sticky header with item thumbnails */}
      <section className="container-x sticky top-[64px] z-30 bg-[#0B110F]/85 backdrop-blur-xl py-3 -mx-4 px-4 sm:mx-0 sm:px-0 border-b border-[#1F3A2E]">
        <div className="grid gap-3" style={{ gridTemplateColumns: `200px repeat(${items.length}, minmax(0, 1fr))` }}>
          <div className="text-xs uppercase tracking-wider text-[#A8B3AD] flex items-center">Eigenschap</div>
          {items.map((h) => (
            <div key={h.id} className="relative" data-testid={`compare-col-${h.sku}`}>
              <button
                onClick={() => removeCompare(h.id)}
                className="absolute -top-1 -right-1 z-10 bg-[#0B110F] border border-[#1F3A2E] rounded-full p-1 text-[#A8B3AD] hover:text-white hover:border-[#D97742]"
                aria-label="Verwijderen"
                data-testid={`compare-page-remove-${h.sku}`}
              >
                <X className="w-3 h-3" />
              </button>
              <div className="bg-[#0F1816] border border-[#1F3A2E] rounded-lg p-2 flex items-center gap-2">
                {h.image_url && <img src={h.image_url} alt={h.name} className="w-14 h-14 object-contain rounded bg-[#0a1410]" />}
                <div className="min-w-0">
                  <div className="font-semibold text-white text-sm leading-tight line-clamp-2">{h.name}</div>
                  <div className="font-mono text-[10px] text-[#A8B3AD] mt-0.5">{h.sku}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Rows */}
      <section className="container-x mt-6">
        <div className="card-surface overflow-hidden">
          {rows.map((row, ri) => (
            <div
              key={row.key}
              className={`grid gap-3 px-4 py-3 border-b border-[#1F3A2E] last:border-b-0 ${row.diff ? "bg-[#D97742]/5" : ""}`}
              style={{ gridTemplateColumns: `200px repeat(${items.length}, minmax(0, 1fr))` }}
              data-testid={`compare-row-${row.key}`}
            >
              <div className="text-xs uppercase tracking-wider text-[#A8B3AD] flex items-center gap-1.5 self-center">
                {row.icon && <row.icon className="w-3 h-3" />}
                {row.label}
                {row.diff && <span className="text-[9px] text-[#D97742] font-mono uppercase">verschilt</span>}
              </div>
              {row.values.map((v, vi) => (
                <div key={vi} className="text-sm text-white flex items-center gap-1.5 flex-wrap">
                  {v}
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* CTA row, add to cart per item */}
      <section className="container-x mt-6">
        <div className="grid gap-3" style={{ gridTemplateColumns: `200px repeat(${items.length}, minmax(0, 1fr))` }}>
          <div />
          {items.map((h) => (
            <button
              key={h.id}
              onClick={() => addHardwareToCart(h)}
              data-testid={`compare-add-cart-${h.sku}`}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold bg-[#D97742] text-white hover:bg-[#c46939] transition"
            >
              <ShoppingCart className="w-4 h-4" /> Toevoegen
            </button>
          ))}
        </div>
      </section>

      {/* Suggestion */}
      <section className="container-x mt-10">
        <div className="card-surface p-5 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-[#D97742]" />
            <p className="text-sm text-[#A8B3AD]">Geen winnaar gevonden? Probeer onze configurator voor een gepersonaliseerd advies op basis van je gebruikssituatie.</p>
          </div>
          <Link to="/configurator" className="text-sm font-semibold text-[#D97742] hover:text-[#c46939]">
            Configurator openen →
          </Link>
        </div>
      </section>
    </div>
  );
}

// ---------------- helpers ----------------
function buildRows(items, addons, leaseSettings, formatPrice) {
  const PLACEMENT_LABEL = { indoor: "Binnen", outdoor: "Buiten", both: "Binnen & buiten" };
  const TYPE_LABEL = { wall: "Wand", counter: "Op balie", freestanding: "Op voet", tablet: "Tablet" };

  const rawRows = [
    {
      key: "vendor",
      label: "Leverancier",
      values: items.map((h) => <span className="capitalize">{h.vendor_slug || "—"}</span>),
      raw: items.map((h) => h.vendor_slug),
    },
    {
      key: "placement",
      label: "Plaatsing",
      values: items.map((h) => (
        <span className="chip chip-outline text-xs">{PLACEMENT_LABEL[h.placement] || h.placement || "—"}</span>
      )),
      raw: items.map((h) => h.placement),
    },
    {
      key: "type",
      label: "Type / montage",
      values: items.map((h) => (
        <span className="chip chip-outline text-xs">{TYPE_LABEL[h.type] || h.type || "—"}</span>
      )),
      raw: items.map((h) => h.type),
    },
    {
      key: "screen",
      label: "Scherm",
      values: items.map((h) => h.screen_size_inch ? `${h.screen_size_inch}"` : (h.name.match(/(\d+(?:\.\d+)?)"/)?.[0] || "—")),
      raw: items.map((h) => h.screen_size_inch || (h.name.match(/(\d+(?:\.\d+)?)/)?.[0])),
    },
    {
      key: "price",
      label: "Bruto (eenmalig)",
      values: items.map((h) => (
        <span className="font-semibold text-white">{formatPrice(h.purchase_price)}</span>
      )),
      raw: items.map((h) => h.purchase_price),
    },
    {
      key: "lease",
      label: "Lease vanaf",
      values: items.map((h) => {
        const m = computeMonthly(h.purchase_price || 0, leaseSettings).monthly;
        return <span className="text-[#7FD8B8]">{formatPrice(m)}/mnd</span>;
      }),
      raw: items.map((h) => Math.round(computeMonthly(h.purchase_price || 0, leaseSettings).monthly)),
    },
    {
      key: "compatible_addons",
      label: "Compatible addons",
      values: items.map((h) => {
        const ids = h.compatible_addon_ids || [];
        return <span className="text-[#A8B3AD] text-xs">{ids.length} add-ons</span>;
      }),
      raw: items.map((h) => (h.compatible_addon_ids || []).length),
    },
    {
      key: "outdoor_rated",
      label: "Outdoor IP-gerated",
      values: items.map((h) => h.placement === "outdoor" || h.placement === "both" ? (
        <Check className="w-4 h-4 text-[#7FD8B8]" />
      ) : <span className="text-[#5d6a64]">—</span>),
      raw: items.map((h) => h.placement === "outdoor" || h.placement === "both"),
    },
    {
      key: "vendor_lead_time",
      label: "Levertijd",
      values: items.map((h) => h.lead_time_weeks ? `${h.lead_time_weeks} wkn` : "Op aanvraag"),
      raw: items.map((h) => h.lead_time_weeks || null),
    },
  ];

  // Compute diff: a row is "different" if not all raw values are equal
  return rawRows.map((r) => ({
    ...r,
    diff: new Set(r.raw.map((x) => JSON.stringify(x))).size > 1,
  }));
}
